import React, {createContext, useContext, useState} from 'react';
import jwtAxios from '../../@crema/services/auth/jwt-auth/jwt-api';
import {
  getFilterQueryString,
  getOffsetString,
  getSortString,
} from '../../utility/utility/filter';
import {SortType} from '../../utility/types/table';
import {FilterOperator} from '../../utility/types/filter';
import {
  NotificationsActionsContextType,
  NotificationsContextType,
  NotificationsFilter,
} from './types';
import moment from 'moment/moment';
import {useJWTAuth} from '../../@crema/services/auth/jwt-auth/JWTAuthProvider';

const defaultNotificationsContext = {
  filters: {
    createdAtFrom: {
      value: moment().startOf('month').format('YYYY-MM-DD'),
      operator: FilterOperator.GTEQ,
    },
    createdAtTo: {
      value: moment().endOf('month').format('YYYY-MM-DD'),
      operator: FilterOperator.LTEQ,
    },
    user: {value: undefined, operator: FilterOperator.EQUAL},
  },
  loadingNotifications: false,
  pagination: {
    page: 1,
    pageSize: 50,
  },
  sort: {
    'notification.createdAt': 'DESC',
  },
  notifications: {
    data: [],
    total: 0,
  },
  notViewedCount: 0,
};

const NotificationsContext = createContext<NotificationsContextType>(
  defaultNotificationsContext,
);

const NotificationsActionsContext =
  createContext<NotificationsActionsContextType>({
    handleFiltersChanged: () => {},
    handleFiltersReset: () => {},
    handlePageChanged: () => {},
    handlePagingChanged: () => {},
    handlePageSizeChanged: () => {},
    handleSortChanged: () => {},
    fetchNotifications: () => {},
    setFilters: () => {},
    getNotViewedNotificationsCount: () => {},
    setViewed: () => {},
  });

export const useNotificationsContext = () => useContext(NotificationsContext);
export const useNotificationsActionsContext = () =>
  useContext(NotificationsActionsContext);

const NotificationsProvider = ({children}: any) => {
  const {user} = useJWTAuth();
  const [notifications, setNotifications] = useState(
    defaultNotificationsContext.notifications,
  );
  const [notViewedCount, setNotViewedCount] = useState(
    defaultNotificationsContext.notViewedCount,
  );
  const [filters, setFilters] = useState<NotificationsFilter>({
    ...defaultNotificationsContext.filters,
    user: {value: user?.user.id, operator: FilterOperator.EQUAL},
  });
  const [loadingNotifications, setLoadingNotifications] = useState(
    defaultNotificationsContext.loadingNotifications,
  );
  const [pagination, setPagination] = useState(
    defaultNotificationsContext.pagination,
  );
  const [sort, setSort] = useState<SortType>(defaultNotificationsContext.sort);

  const fetchNotifications = () => {
    setLoadingNotifications(true);
    Promise.all([
      getNotifications().then(({data}) => {
        setNotifications({
          data: data.data.notifications,
          total: data.data.total,
        });
      }),
      getNotViewedNotificationsCount().then(({data}) => {
        setNotViewedCount(data.data.count);
      }),
    ])
      .then(() => {
        setLoadingNotifications(false);
      })
      .catch(() => {
        setLoadingNotifications(false);
      });
  };

  const getNotifications = async () => {
    const queryString = getQueryString();

    return jwtAxios.get(`/notification/list${queryString}`);
  };

  const getNotViewedNotificationsCount = async () => {
    return jwtAxios.get(`/notification/not-viewed/count/get/${user?.user.id}`);
  };

  const handlePagingChanged = (page: number, pageSize: number) => {
    setPagination({...pagination, page, pageSize});
  };

  const setViewed = async (notificationIds: string[]) => {
    return jwtAxios.patch(`/notification/viewed/update`, {notificationIds});
  };

  const getQueryString = () => {
    let queryString = '';
    const filterQueryString = getFilterQueryString(filters);
    const offsetString = getOffsetString(pagination.page, pagination.pageSize);
    const sortString = getSortString(sort);

    return queryString.concat(
      '?',
      offsetString,
      sortString ? `&${sortString}` : sortString,
      filterQueryString ? `&${filterQueryString}` : filterQueryString,
    );
  };

  const handleFiltersChanged = () => {
    setFilters({
      ...filters,
    });
  };

  const handleFiltersReset = () => {
    setFilters(defaultNotificationsContext.filters);
  };

  const handlePageChanged = (page: number, pageSize: number) => {
    setPagination({...pagination, page, pageSize});
  };

  const handlePageSizeChanged = (page: number, pageSize: number) => {
    setPagination({...pagination, page, pageSize});
  };

  const handleSortChanged = (sort: SortType) => {
    setSort(sort);
  };

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        notViewedCount,
        loadingNotifications,
        filters,
        pagination,
        sort,
      }}>
      <NotificationsActionsContext.Provider
        value={{
          handleFiltersChanged,
          handleFiltersReset,
          handlePageChanged,
          handlePageSizeChanged,
          handleSortChanged,
          fetchNotifications,
          setFilters,
          handlePagingChanged,
          getNotViewedNotificationsCount,
          setViewed,
        }}>
        {children}
      </NotificationsActionsContext.Provider>
    </NotificationsContext.Provider>
  );
};

export default NotificationsProvider;
